import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { Tag } from '@/design-system/tag';
import { toast } from '@/design-system/toaster';

import { warehouseApi } from '../../api/warehouse.api';
import {
  DocumentUpload,
  type DocumentValue,
} from '../../components/DocumentUpload';
import { ActionDialog } from '../../components/dialogs';
import { FormField, TextArea, TextInput } from '../../components/form';
import { ClientSelect, HubSelect } from '../../components/pickers';
import { OpnameBatchSelect } from '../../components/StockBatchSelect';
import { StorageSelect } from '../../components/StorageSelect';
import {
  DetailHeader,
  HubRequired,
  KV,
  KVGrid,
  linkCls,
  metaCls,
  Section,
  StockTypeBadge,
} from '../../components/ui';
import { WizardFooter, WizardSteps } from '../../components/wizard';
import { fmtQty } from '../../format';
import { useServerErrors } from '../../hooks/useServerErrors';
import { useWizard } from '../../hooks/useWizard';
import { WAREHOUSE_PATHS } from '../../paths';
import { STOCK_TYPE_META } from '../../status';
import {
  type ClientRef,
  type FieldError,
  type OpnameBatch,
  type OpnameImportLine,
} from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { DiscrepancySummary } from './DiscrepancySummary';
import { NewBatchRow } from './NewBatchRow';
import { OpnameCsvImportModal } from './OpnameCsvImportModal';
import { OpnameLinesTable } from './OpnameLinesTable';
import {
  checkInfo,
  checkLines,
  type DraftLine,
  fmtGap,
  gapCls,
  gapOf,
  isPicked,
  lineFromBatch,
  lineFromImport,
  lineLotKey,
  type LockedRow,
  lockedRowsOf,
  newLine,
  OPNAME_CODE_FIELD,
  opnameFieldOf,
  opnameStepOf,
  physicalIssue,
  reviewLines,
  summarize,
  toPayload,
} from './opnameDraft';

const STEPS = [
  { id: 1, label: 'Informasi utama' },
  { id: 2, label: 'Daftar produk' },
  { id: 3, label: 'Review' },
];

const InlineError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-1 text-[11px] text-(--state-error-base)" role="alert">
    {children}
  </p>
);

/** A small captioned value of a row's count grid. */
const Cell: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="text-[11px] font-medium text-text-soft-400">{label}</span>
    {children}
  </div>
);

/* --------------------------------- form ---------------------------------- */

interface OpnameFormProps {
  hubID: number;
  hubName: string | undefined;
}

const OpnameForm: React.FC<OpnameFormProps> = ({ hubID, hubName }) => {
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientRef | null>(null);
  const [notes, setNotes] = useState('');
  const [report, setReport] = useState<DocumentValue | null>(null);
  const [uploading, setUploading] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([]);
  /** Narrows every row's batch picker to one storage ('' = all). */
  const [storageFilter, setStorageFilter] = useState('');
  /** A client the user picked while rows exist: waits for confirmation. */
  const [pendingClient, setPendingClient] = useState<ClientRef | null>(null);
  const [importing, setImporting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blockedNote, setBlockedNote] = useState<string | null>(null);
  const [locked, setLocked] = useState<LockedRow[]>([]);
  const server = useServerErrors(opnameFieldOf, opnameStepOf);
  const { errors, setErrors, fieldError: fe, lineError } = server;

  const picked = lines.filter(isPicked);
  const hasRows = lines.length > 0;
  const totals = summarize(lines);
  const hasGap = totals.gapLineCount > 0;

  const wizard = useWizard<FieldError>({
    total: STEPS.length,
    check: (s) =>
      s === 1 ? checkInfo(client, report) : s === 2 ? checkLines(lines) : [],
  });
  const { step } = wizard;

  const clearFeedback = () => {
    setErrors([]);
    setLocked([]);
    setBlockedNote(null);
  };
  const next = () => {
    setLocked([]);
    setErrors(wizard.next());
  };
  const back = () => {
    clearFeedback();
    wizard.back();
  };

  const applyClient = (c: ClientRef | null) => {
    setClient(c);
    // The rows were stock of the old client.
    setLines([]);
    setStorageFilter('');
    clearFeedback();
  };
  const pickClient = (c: ClientRef | null) => {
    if (c?.id === client?.id) return;
    if (hasRows && c) setPendingClient(c);
    else applyClient(c);
  };

  const editLine = (key: number, patch: Partial<DraftLine>) => {
    setLines((prev) =>
      prev.map((l) => (l.key === key ? { ...l, ...patch } : l))
    );
    clearFeedback();
  };
  const addLine = (mode: 'existing' | 'new') => {
    setLines((prev) => [...prev, newLine(mode)]);
    clearFeedback();
  };
  const removeLine = (key: number) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
    clearFeedback();
  };

  const submit = async () => {
    if (!client || !report) return;
    setBusy(true);
    clearFeedback();
    try {
      const created = await warehouseApi.createStockOpname(
        toPayload({
          hubID,
          hubName,
          client,
          notes,
          reportUrl: report.url,
          lines,
        })
      );
      toast.success(
        created.status === 'DONE'
          ? 'Stock opname selesai tanpa penyesuaian'
          : 'Stock opname diajukan',
        { description: created.code }
      );
      navigate(`${WAREHOUSE_PATHS.stockOpname}/${created.id}`);
    } catch (err) {
      setConfirming(false);
      const rows = lockedRowsOf(err);
      if (rows.length) {
        // Another open opname already counts these (storage, SKU) pairs.
        setLocked(rows);
        wizard.goTo(2);
        return;
      }
      // 422 field errors → inline at their field / row, back to that step.
      const shown = server.fromError(err, OPNAME_CODE_FIELD);
      if (shown.step !== null) wizard.goTo(shown.step);
      else {
        setErrors([{ field: '', message: shown.message }]);
        wizard.goTo(3);
      }
    } finally {
      setBusy(false);
    }
  };

  /** Submit: local checks first, then the confirmation. */
  const askSubmit = () => {
    const problems = wizard.checkAll();
    if (problems.length) {
      setErrors(problems);
      return;
    }
    setConfirming(true);
  };

  const errorAlert = errors.length > 0 && (
    <Alert
      status="error"
      appearance="stroke"
      size="lg"
      title={`${errors.length} isian perlu diperbaiki`}
      data-testid="opname-errors"
    >
      <ul className="list-disc pl-4">
        {errors.map((e, i) => (
          <li key={`${i}-${e.message}`}>{e.message}</li>
        ))}
      </ul>
    </Alert>
  );
  const lockedAlert = locked.length > 0 && (
    <Alert
      status="error"
      appearance="stroke"
      size="lg"
      title="Sebagian stok sedang di-opname"
      data-testid="opname-locked"
    >
      <p>
        Storage dan SKU berikut sedang dihitung di opname lain yang belum
        selesai. Hapus barisnya atau tunggu opname tersebut selesai.
      </p>
      <ul className="mt-1 list-disc pl-4">
        {locked.map((r, i) => (
          <li key={`${i}-${r.sku}-${r.opnameCode}`}>
            {r.lineIndex !== null ? `Baris ${r.lineIndex + 1} · ` : ''}
            {r.sku} — sedang di-opname di {r.opnameCode}
          </li>
        ))}
      </ul>
    </Alert>
  );

  const lockedAt = (i: number) => locked.find((r) => r.lineIndex === i);

  return (
    <div className="flex flex-col gap-5">
      <WizardSteps steps={STEPS} current={step} />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          {step === 1 && (
            <Section title="Informasi utama">
              <div className="flex flex-col gap-3">
                {errorAlert}
                <FormField label="Hub" htmlFor="opname-hub">
                  <TextInput
                    id="opname-hub"
                    size="md"
                    value={hubName ?? `Hub #${hubID}`}
                    onChange={() => undefined}
                    disabled
                  />
                </FormField>
                <FormField
                  label="Client"
                  required
                  error={fe('client')}
                  hint="Satu opname menghitung stok satu client."
                  testId="opname-client"
                >
                  <ClientSelect
                    value={client}
                    onChange={pickClient}
                    invalid={!!fe('client')}
                  />
                </FormField>
                <FormField
                  label="Catatan"
                  htmlFor="opname-notes"
                  optional
                  error={fe('notes')}
                >
                  <TextArea
                    id="opname-notes"
                    rows={3}
                    value={notes}
                    onChange={setNotes}
                    placeholder="Mis. hitung fisik akhir bulan, audit area karantina"
                  />
                </FormField>
                <FormField
                  label="Berita acara"
                  required
                  error={fe('report')}
                  hint="PDF berita acara atau lembar hitung yang ditandatangani, maksimal 10 MB."
                >
                  <DocumentUpload
                    value={report}
                    onChange={(doc) => {
                      setReport(doc);
                      setErrors([]);
                    }}
                    onBusyChange={setUploading}
                    title="Pilih PDF berita acara atau seret ke sini"
                    testId="opname-report"
                  />
                </FormField>
              </div>
            </Section>
          )}

          {step === 2 && (
            <Section
              title="Daftar produk"
              description={`Stok ${client?.name ?? 'client'} yang dihitung fisik`}
              right={
                <Button
                  tone="neutral"
                  style="stroke"
                  size="xs"
                  data-testid="opname-import-open"
                  onClick={() => setImporting(true)}
                  leftIcon={<i className="ri-upload-2-line text-[14px]" />}
                >
                  Import CSV
                </Button>
              }
            >
              <div className="flex flex-col gap-3" data-testid="opname-rows">
                {errorAlert}
                {lockedAlert}
                {blockedNote && (
                  <Alert
                    status="warning"
                    appearance="stroke"
                    size="sm"
                    title={blockedNote}
                    data-testid="opname-blocked"
                  />
                )}
                <FormField
                  label="Filter storage"
                  optional
                  hint="Mempersempit pilihan batch ke satu storage."
                >
                  <StorageSelect
                    testId="opname-storage-filter"
                    hubID={hubID}
                    value={storageFilter}
                    onChange={(id) => setStorageFilter(id)}
                    onClear={() => setStorageFilter('')}
                    placeholder="Semua storage"
                  />
                </FormField>
                {!hasRows && (
                  <p
                    className="rounded-lg border border-dashed border-stroke-soft-200 px-4 py-6 text-center text-sm text-text-sub-600"
                    data-testid="opname-empty"
                  >
                    Belum ada produk yang dihitung. Tambahkan dari stok sistem,
                    catat batch baru, atau impor lembar hitung dari CSV.
                  </p>
                )}
                {lines.map((l, i) => {
                  const n = i + 1;
                  const others = new Set(
                    lines
                      .filter((x) => x.key !== l.key)
                      .map(lineLotKey)
                      .filter(Boolean)
                  );
                  const lock = lockedAt(i);
                  const rowError =
                    lineError(i) ??
                    (lock
                      ? `${lock.sku} sedang di-opname di ${lock.opnameCode}.`
                      : null) ??
                    (isPicked(l) ? physicalIssue(l) : null);
                  const identityErr = !isPicked(l) && !!lineError(i);
                  const gap = gapOf(l);
                  return (
                    <div
                      key={l.key}
                      className="flex flex-col gap-2 rounded-lg border border-stroke-soft-200 p-3"
                      data-testid={`opname-line-${i}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                          Baris {n}
                          {l.mode === 'new' && (
                            <Tag size="xs" variant="stroke">
                              Batch baru
                            </Tag>
                          )}
                          {l.mode === 'existing' && (
                            <StockTypeBadge type={l.stockType} />
                          )}
                        </span>
                        <Button
                          tone="neutral"
                          style="ghost"
                          size="icon-sm"
                          title="Hapus baris"
                          aria-label={`Hapus baris ${n}`}
                          onClick={() => removeLine(l.key)}
                        >
                          <i className="ri-delete-bin-line text-[14px]" />
                        </Button>
                      </div>
                      {l.mode === 'existing' ? (
                        <OpnameBatchSelect
                          aria-label={`Produk & batch baris ${n}`}
                          testId={`opname-batch-${i}`}
                          hubID={hubID}
                          clientID={client?.id ?? null}
                          storageID={storageFilter}
                          value={lineLotKey(l)}
                          selectedLabel={
                            isPicked(l)
                              ? [
                                  l.storageName,
                                  l.sku,
                                  l.batchNumber,
                                  ...(l.stockType
                                    ? [STOCK_TYPE_META[l.stockType].label]
                                    : []),
                                ].join(' · ')
                              : undefined
                          }
                          excludeKeys={others}
                          invalid={identityErr}
                          onPick={(b: OpnameBatch) => {
                            editLine(l.key, lineFromBatch(l, b));
                          }}
                          onBlocked={(b) =>
                            setBlockedNote(
                              `${b.sku} · ${b.batchNumber} sedang di-opname di ${b.lockedBy ?? 'opname lain'} dan tidak bisa dihitung di sini.`
                            )
                          }
                        />
                      ) : (
                        <NewBatchRow
                          hubID={hubID}
                          clientID={client?.id ?? null}
                          line={l}
                          n={n}
                          index={i}
                          skuInvalid={!l.sku && !!lineError(i)}
                          storageInvalid={
                            !!l.sku && !l.storageID && !!lineError(i)
                          }
                          onChange={(patch) => editLine(l.key, patch)}
                        />
                      )}
                      {isPicked(l) && l.mode === 'existing' && (
                        <p className={metaCls}>
                          {l.itemName} · {l.storagePath || l.storageName}
                        </p>
                      )}
                      {l.mode === 'new' && l.sku && (
                        <p className={metaCls}>
                          {l.itemName} · batch dibuat saat disetujui FAT
                        </p>
                      )}
                      <div className="grid grid-cols-2 items-start gap-2 sm:grid-cols-[96px_132px_96px_56px_minmax(0,1fr)]">
                        <Cell label="Sistem">
                          <span
                            className="flex h-9 items-center justify-end font-mono text-[13px] tabular-nums text-text-strong-950"
                            data-testid={`opname-system-${i}`}
                          >
                            {fmtQty(l.qtySystem)}
                          </span>
                        </Cell>
                        <Cell label="Fisik">
                          <TextInput
                            aria-label={`Qty fisik baris ${n}`}
                            data-testid={`opname-physical-${i}`}
                            size="md"
                            inputMode="decimal"
                            value={l.physical}
                            disabled={!isPicked(l)}
                            invalid={!!rowError && !identityErr}
                            placeholder="0"
                            onChange={(v) => editLine(l.key, { physical: v })}
                            className="text-right"
                          />
                        </Cell>
                        <Cell label="Selisih">
                          <span
                            className={`flex h-9 items-center justify-end font-mono text-[13px] font-semibold tabular-nums ${gap === null ? 'text-text-soft-400' : gapCls(gap)}`}
                            data-testid={`opname-gap-${i}`}
                          >
                            {gap === null ? '—' : fmtGap(gap)}
                          </span>
                        </Cell>
                        <Cell label="UoM">
                          <span className="flex h-9 items-center text-[12px] text-text-sub-600">
                            {l.uomName}
                          </span>
                        </Cell>
                        <Cell label="Catatan">
                          <TextInput
                            aria-label={`Catatan baris ${n}`}
                            data-testid={`opname-notes-${i}`}
                            size="md"
                            value={l.notes}
                            disabled={!isPicked(l)}
                            placeholder="Opsional"
                            onChange={(v) => editLine(l.key, { notes: v })}
                          />
                        </Cell>
                      </div>
                      {rowError && <InlineError>{rowError}</InlineError>}
                    </div>
                  );
                })}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    tone="neutral"
                    style="stroke"
                    size="xs"
                    data-testid="opname-add-line"
                    disabled={!client}
                    onClick={() => addLine('existing')}
                    leftIcon={<i className="ri-add-line text-[14px]" />}
                  >
                    Tambah produk
                  </Button>
                  <Button
                    tone="neutral"
                    style="stroke"
                    size="xs"
                    data-testid="opname-add-new-batch"
                    disabled={!client}
                    onClick={() => addLine('new')}
                    leftIcon={<i className="ri-price-tag-3-line text-[14px]" />}
                  >
                    Batch baru
                  </Button>
                </div>
                {fe('lines') && <InlineError>{fe('lines')}</InlineError>}
                {picked.length > 0 && <DiscrepancySummary totals={totals} />}
              </div>
            </Section>
          )}

          {step === 3 && (
            <Section title="Review">
              <div className="flex flex-col gap-4" data-testid="opname-review">
                {errorAlert}
                <KVGrid
                  items={[
                    ['Hub', hubName ?? `Hub #${hubID}`],
                    ['Client', client?.name],
                    ['Catatan', notes.trim() || null],
                    [
                      'Berita acara',
                      report ? (
                        <a
                          href={report.url}
                          target="_blank"
                          rel="noreferrer"
                          className={linkCls}
                        >
                          {report.name}
                        </a>
                      ) : null,
                    ],
                  ]}
                />
                <DiscrepancySummary totals={totals} />
                <OpnameLinesTable lines={reviewLines(lines)} />
                <Alert
                  status="information"
                  appearance="stroke"
                  size="lg"
                  title={
                    hasGap ? 'Selisih perlu persetujuan' : 'Tidak ada selisih'
                  }
                >
                  {hasGap
                    ? 'Opname dengan selisih menunggu persetujuan manager, lalu FAT. Stok baru disesuaikan setelah FAT menyetujui.'
                    : 'Semua hitungan fisik sama dengan sistem. Opname langsung selesai tanpa penyesuaian stok.'}
                </Alert>
              </div>
            </Section>
          )}

          <WizardFooter
            step={step}
            total={STEPS.length}
            onBack={back}
            onNext={next}
            onSubmit={askSubmit}
            submitLabel="Ajukan stock opname"
            submitTestId="opname-submit"
            busy={busy}
            nextDisabled={step === 1 && uploading}
            nextDisabledReason="Berita acara sedang diunggah"
            onCancel={() => navigate(WAREHOUSE_PATHS.stockOpname)}
          />
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Ringkasan">
            <KV label="Hub" value={hubName ?? `Hub #${hubID}`} />
            <KV label="Client" value={client?.name} />
            <KV
              label="Baris dihitung"
              value={totals.lineCount ? String(totals.lineCount) : null}
            />
            <KV
              label="Baris berselisih"
              value={totals.lineCount ? String(totals.gapLineCount) : null}
            />
            <KV
              label="Total selisih"
              value={
                totals.lineCount ? (
                  <span className={gapCls(totals.totalGap)}>
                    {fmtGap(totals.totalGap)}
                  </span>
                ) : null
              }
            />
          </Section>
        </div>
      </div>

      <OpnameCsvImportModal
        open={importing}
        hubID={hubID}
        client={client}
        replaces={hasRows}
        onClose={() => setImporting(false)}
        onApply={(rows: OpnameImportLine[]) => {
          setLines(rows.map(lineFromImport));
          setImporting(false);
          clearFeedback();
        }}
      />
      <ActionDialog
        open={!!pendingClient}
        title="Ganti client"
        subtitle={pendingClient?.name}
        description="Stok dihitung per client, jadi daftar produk yang sudah diisi akan dikosongkan."
        confirmLabel="Ganti & kosongkan"
        tone="destructive"
        testId="opname-client-dialog"
        onConfirm={() => {
          if (pendingClient) applyClient(pendingClient);
          setPendingClient(null);
        }}
        onClose={() => setPendingClient(null)}
      />
      <ActionDialog
        open={confirming}
        title="Ajukan stock opname"
        subtitle={client?.name}
        description={
          hasGap
            ? `Terdapat selisih pada ${totals.gapLineCount} baris (total ${fmtGap(totals.totalGap)}). Opname akan menunggu persetujuan manager, lalu FAT, sebelum stok disesuaikan.`
            : 'Tidak ada selisih antara hitungan fisik dan sistem. Opname langsung selesai tanpa penyesuaian stok.'
        }
        confirmLabel="Ajukan opname"
        busy={busy}
        testId="opname-submit-dialog"
        onConfirm={() => void submit()}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
};

/* --------------------------------- page ---------------------------------- */

const StockOpnameNewPage: React.FC = () => {
  const { hubID, hub, setHubID } = useWarehouse();
  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.stockOpname}
        backLabel="Stock opname"
        title="Buat stock opname"
        actions={
          hubID === null ? (
            <HubSelect
              value={hubID}
              onChange={setHubID}
              className="w-48"
              aria-label="Hub"
            />
          ) : undefined
        }
      />
      {hubID === null ? (
        <HubRequired />
      ) : (
        // A new hub starts the form over: its storages are not this hub's.
        <OpnameForm key={hubID} hubID={hubID} hubName={hub?.name} />
      )}
    </div>
  );
};

export default StockOpnameNewPage;
